import type { Item } from "./Item";
import type { LooseAutocomplete, ValueOf } from "./Util";
import type { S25WsNode } from "./S25WsNode";
import type { Proto } from "./Proto";
import type { Doc } from "../modules/system-settings/document-management/s25.document.const";
import type { DocumentListItem } from "../services/document.service";
type CharBoolean = Proto.CharBoolean;
type NumericalString = Proto.NumericalString;
type LowerStringBoolean = Proto.LowerStringBoolean;

export namespace Report {
    export interface Data {
        subject: "Report";
        id: Item.Ids.Report;
    }

    export interface SimpleObject {
        rpt_name?: string;
        rpt_use?: Use;
        rpt_id: number;
        rpt_engine: Engine;
        object_type?: ObjectType;
        document_id?: number;
        optUUID?: number;
        itemID?: number;
        type?: Doc.TypeId;
        isAsync?: boolean;
        rpt_filename_runtime?: string;
    }

    // TODO: rename to Obj or something to avoid JS Object collision
    export type Object = S25WsNode & {
        favorite: CharBoolean;
        href: string;
        last_mod_dt: string;
        last_mod_user: string;
        object_type: ObjectType;
        report_engine: Engine;
        report_filename: string;
        report_group_id: NumericalString | number;
        report_id: NumericalString | number;
        report_name: string;
        document_id?: number;
        content_disposition?: string;
        itemId?: number | NumericalString;
        itemName?: string;
    };

    export namespace Obj {
        export type Normalized = Object & { itemId: number | NumericalString };
        export function normalizeReports(reports: Object[], sortByObjectType?: boolean): Normalized[] {
            for (const report of reports) {
                // Set DM report name more meaningful
                if (report.report_engine === "DM" && !ShellReportIds.has(Number(report.report_id))) {
                    switch (report.object_type) {
                        case ObjectType.EventDocument:
                            report.report_name = "Event Document: " + report.report_name;
                            break;
                        case ObjectType.PaymentDocument:
                            report.report_name = `Invoice Document: ${report.report_name}`;
                            break;
                        case ObjectType.ReservationDocument:
                            report.report_name = "Reservation Document: " + report.report_name;
                            break;
                        case ObjectType.OrganizationDocument:
                            report.report_name = "Organization Document: " + report.report_name;
                            break;
                    }
                }

                report.itemId = report.report_id;
                report.itemName = report.report_name;
            }

            if (sortByObjectType) {
                const objectTypeOrder = [
                    ObjectType.Event,
                    ObjectType.EventDocument,
                    ObjectType.OrganizationDocument,
                    ObjectType.ReservationDocument,
                ];
                const order = new Map<number, number>(objectTypeOrder.map((id, i) => [id, i]));
                reports.sort((a: Report.Object, b: Report.Object) => {
                    const aVal = Number(a.object_type);
                    const bVal = Number(b.object_type);
                    if (!order.has(aVal) || !order.has(bVal)) {
                        return aVal - bVal;
                    }
                    return order.get(aVal)! - order.get(bVal)!;
                });
            }

            return reports as Report.Obj.Normalized[];
        }
    }

    export type Meta = S25WsNode & {
        async: LowerStringBoolean;
        content_disposition: Object["content_disposition"];
        mime_type: string;
        report_run: Run[] & { [key: string]: any };
        pipeline: LooseAutocomplete<"PDF">;
        favorite: Object["favorite"];
        object_type: Object["object_type"];
        report_engine: Object["report_engine"];
        report_filename: Object["report_filename"];
        report_group_id: Object["report_group_id"];
        report_id: Object["report_id"];
        report_name: Object["report_name"];
        parameter?: Parameter[];
        rpt_name?: Object["report_name"];
        document_id?: Object["document_id"];
    };

    export type Run = S25WsNode & {
        false_value: string;
        true_value: string;
        report_run_id: NumericalString;
        rpt_run_name: string;
    };

    export type Context = {
        num_parm1?: any;
        num_parm2?: NumericalString;
        num_parm3?: NumericalString;
        num_parm4?: NumericalString;
        char_parm4?: string;

        [key: string]: any;
    };

    export type Parameter = S25WsNode & {
        parameter_field_mapping: string;
        parameter_name: string;
        parameter_number: string;
        parameter_type: ParameterType;
        required: CharBoolean;
        sort_order: NumericalString;
        validate?: string;
        validate_type?: ParameterValidateType;
    };

    export type ParameterValue = string | number;

    export type Engine = "JR" | "WS" | "DM";

    export const Use = {
        None: 0,
        Confirmation: 1,
        Invoice: 2,
        Payment: 4,
    } as const;
    export type Use = ValueOf<typeof Use>;

    export const ObjectType = {
        NonSpecific: 0,
        Event: 1,
        Organization: 2,
        Contact: 3,
        Space: 4,
        Resource: 6,
        EventDocument: 10,
        PaymentDocument: 11,
        ReservationDocument: 12,
        OrganizationDocument: 13,
        EventListingDocument: 14,
        ReservationListingDocument: 15,
        LocationListingDocument: 16,
        ResourceListingDocument: 17,
        OrganizationListingDocument: 18,
        InvoiceExcel: 19,
        TaskListing: 20,
        Reservations: 21,
    } as const;
    export type ObjectType = ValueOf<typeof ObjectType>;

    export const Reports = {
        LocationDailySheet: { id: -147, object_type: ObjectType.Space },
        InvoiceRelated: { id: -149, object_type: ObjectType.Event },
        EventListing: { id: -171, object_type: ObjectType.EventListingDocument },
        LocationListing: { id: -179, object_type: ObjectType.LocationListingDocument },
        ResourceListing: { id: -180, object_type: ObjectType.ResourceListingDocument },
        OrganizationListing: { id: -181, object_type: ObjectType.OrganizationListingDocument },
        EventShell: { id: -198, object_type: ObjectType.Event },
        DefaultInvoice: { id: -199, object_type: ObjectType.Event },
        ReservationShell: { id: -210, object_type: ObjectType.ReservationDocument },
        OrganizationShell: { id: -217, object_type: ObjectType.OrganizationDocument },
        PaymentShell: { id: -236, object_type: ObjectType.PaymentDocument },
        EventListingShell: { id: -228, object_type: ObjectType.EventListingDocument },
        ReservationListingShell: { id: -229, object_type: ObjectType.ReservationListingDocument },
        LocationListingShell: { id: -230, object_type: ObjectType.LocationListingDocument },
        ResourceListingShell: { id: -231, object_type: ObjectType.ReservationListingDocument },
        OrganizationListingShell: { id: -232, object_type: ObjectType.OrganizationDocument },
        InvoiceShell: { id: -237, object_type: ObjectType.InvoiceExcel },
        TaskListing: { id: -238, object_type: ObjectType.TaskListing },
        ReservationsShell: { id: -251, object_type: ObjectType.Reservations },
    } as const;

    export const reportsById = new Map(Object.values(Reports).map((report) => [report.id, report]));

    export const ShellReportIds: ReadonlySet<number> = new Set<number>([
        Reports.EventShell.id,
        Reports.ReservationShell.id,
        Reports.OrganizationShell.id,
        Reports.PaymentShell.id,
    ]);

    const ShellDocumentTypes: ReadonlyMap<number, { label: string; objectType: ObjectType }> = new Map([
        [Reports.EventShell.id, { label: "Event Document", objectType: ObjectType.EventDocument }],
        [Reports.OrganizationShell.id, { label: "Organization Document", objectType: ObjectType.OrganizationDocument }],
        [Reports.ReservationShell.id, { label: "Reservation Document", objectType: ObjectType.ReservationDocument }],
        [Reports.PaymentShell.id, { label: "Invoice Document", objectType: ObjectType.PaymentDocument }],
    ]);

    export function shellDocumentReports(
        shell: SimpleObject | undefined,
        documents: DocumentListItem[],
    ): SimpleObject[] {
        const documentType = shell && ShellDocumentTypes.get(Number(shell.rpt_id));
        if (!documentType) return [];

        return documents.map((document) => ({
            ...shell,
            rpt_id: document.reportId || shell.rpt_id,
            rpt_name: `${documentType.label}: ${document.itemName}`,
            document_id: document.itemId,
            optUUID: document.itemId,
            object_type: documentType.objectType,
        }));
    }

    // Maps task queries from search page to names accepted by report service
    export const taskQueryMap = {
        overdue: "overdue",
        outstanding: "outstanding",
        flagged: "flagged",
        completed: "completed",
        assigned_all: "assigned_by_you",
        today_agenda: "todays_agenda",
    } as const;

    export const enum ReportFormat {
        PDF = 2,
        Excel = 4,
        RichText = 6,
        HTML = 1,
        Text = 3,
    }

    export type ParameterType = ValueOf<typeof parameterType>;
    export const parameterType = {
        String: "S",
        Integer: "N",
        Float: "F",
        Date: "D",
        Time: "T",
        DateTime: "E",
        Boolean: "B",
        EventSearch: "QE",
        OrganizationSearch: "QO",
        LocationSearch: "QS",
        ResourceSearch: "QR",
        Object: "O",
        GroupBy: "GB",
        MeetingPatterns: "MP",
    } as const;
    export const validParameterTypes = new Set(Object.values(parameterType));

    export type ParameterValidateType = ValueOf<typeof parameterValidateType>;
    export const parameterValidateType = {
        GroupBy: "Group By",
    } as const;

    export type GroupByConfig = {
        parameterName: string;
        fieldMapping: string;
        options: readonly string[];
        defaultValue: string;
    };

    export type GroupBySelection = {
        fieldMapping: string;
        value: string;
    };

    export function findGroupByParameters(meta: Meta): Parameter[] {
        return (
            meta.parameter?.filter(
                (parameter) =>
                    parameter.parameter_type === parameterType.GroupBy &&
                    parameter.validate_type === parameterValidateType.GroupBy &&
                    parameter.validate,
            ) ?? []
        ).sort((left, right) => Number(left.sort_order) - Number(right.sort_order));
    }

    export function parseGroupByValidate(validate: string): string[] {
        return validate
            .split(",")
            .map((option) => option.trim())
            .filter(Boolean);
    }

    export function toGroupByParameterConfig(parameter: Parameter): GroupByConfig | null {
        if (!parameter.validate) return null;
        const options = parseGroupByValidate(parameter.validate);
        if (!options.length) return null;
        return {
            parameterName: parameter.parameter_name,
            fieldMapping: parameter.parameter_field_mapping,
            options,
            defaultValue: options[0],
        };
    }

    export function toGroupByConfigs(meta: Meta): GroupByConfig[] {
        return findGroupByParameters(meta)
            .map((parameter) => toGroupByParameterConfig(parameter))
            .filter((config): config is GroupByConfig => config !== null);
    }
}
